import { useCallback, useReducer } from 'react';
import {
  createDefaultPermissionState,
  permissionReducer,
  type PermissionState,
} from './permissionState';

export interface UsePermissionStateResult {
  permissions: PermissionState;
  setPermission: <K extends keyof PermissionState>(key: K, value: PermissionState[K]) => void;
  setAllPermissions: (permissions: Partial<PermissionState>) => void;
  resetPermissions: (defaults?: PermissionState) => void;
}

/**
 * Owns the whole role permission state in a single reducer so bulk operations
 * (select all, apply template, load from API) apply atomically in one dispatch.
 */
export const usePermissionState = (initial?: PermissionState): UsePermissionStateResult => {
  const [permissions, dispatch] = useReducer(
    permissionReducer,
    initial ?? createDefaultPermissionState(),
  );

  const setPermission = useCallback(
    <K extends keyof PermissionState>(key: K, value: PermissionState[K]) => {
      dispatch({ type: 'SET_PERMISSION', key, value });
    },
    [],
  );

  const setAllPermissions = useCallback((next: Partial<PermissionState>) => {
    dispatch({ type: 'SET_ALL', permissions: next });
  }, []);

  const resetPermissions = useCallback((defaults?: PermissionState) => {
    dispatch({ type: 'RESET', defaults });
  }, []);

  return { permissions, setPermission, setAllPermissions, resetPermissions };
};
